import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { GlowLayers } from '@/components/auth/GlowInput';
import { OperatorMark } from '@/components/brand/OperatorMark';

interface EmptyStateBrandProps {
  title: string;
  description?: string;
  /** Logo rendered inside the halo tile. Defaults to the OperatorMark.
   *  Pass any 28-px-sized SVG/icon (e.g. <Layers className="h-7 w-7 text-foreground" />). */
  icon?: ReactNode;
  /** Bottom padding on the centering wrapper. The canvas variant uses this
   *  to push the block up so its eyeline matches the chat panel (whose
   *  centering axis sits above the composer). */
  bottomOffsetClass?: string;
  /** Pulses the logo — used by the canvas loading state. */
  loading?: boolean;
}

export function EmptyStateBrand({
  title,
  description,
  icon,
  bottomOffsetClass,
  loading = false,
}: EmptyStateBrandProps) {
  return (
    <div
      className={cn(
        // h-full for direct-height parents (canvas); flex-grow+min-h-0 for
        // flex-column parents (assistant-ui viewport). Works in both.
        'flex h-full w-full flex-grow flex-col items-center justify-center text-center px-6 min-h-0',
        bottomOffsetClass,
      )}
    >
      <div
        className={cn(
          'auth-glow-wrapper is-brand relative flex h-16 w-16 items-center justify-center rounded-full',
          loading && 'animate-pulse',
        )}
      >
        <div className="auth-glow-layers">
          <GlowLayers
            outerRadiusClass="rounded-full"
            midRadiusClass="rounded-full"
            innerRadiusClass="rounded-full"
          />
        </div>
        <div className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-card">
          {icon ?? <OperatorMark className="h-7 w-7 text-foreground" />}
        </div>
      </div>
      <h3 className="mt-5 text-base font-semibold text-foreground">{title}</h3>
      {/* min-h-[2.5rem] reserves space for two lines of description, so the
          chat variant (no description) lines up at the same height as the
          canvas variant (where the description usually wraps to two lines). */}
      <p className="mt-1 min-h-[2.5rem] max-w-xs text-sm text-muted-foreground">
        {description}
      </p>
    </div>
  );
}
